// École Nid Douillet - Protected Route Component
// Route protection based on authentication and roles

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@store/authStore';
import { UserRole } from '@/types/index';
import LoadingSpinner from '@components/ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  permissions?: string[];
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles,
  permissions,
  redirectTo = '/auth/login',
}) => {
  const { isAuthenticated, isLoading, user, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-neutral-600">
            Vérification des autorisations...
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role-based access
  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check permission-based access
  if (permissions && permissions.length > 0) {
    const hasRequiredPermission = permissions.some(permission => hasPermission(permission));
    if (!hasRequiredPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Additional role-specific checks
  if (user) {
    // Parents can only access their own data
    if (user.role === 'PARENT') {
      const parentRestrictedPaths = ['/children', '/parents', '/analytics', '/settings'];
      const currentPath = location.pathname;
      
      if (parentRestrictedPaths.some(path => currentPath.startsWith(path))) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default ProtectedRoute;
